/* eslint-disable no-console */
import { FormType, FormErrors, Locales } from "@simplyk/common";
import { v4 } from "uuid";

import { AppFormProvider } from "../../components/AppProvider/AppFormProvider/AppFormProvider";
import { TicketingHead } from "../../components/Head/TicketingHead/TicketingHead";
import { FrontendAuctionProvider } from "../../contexts/FrontendAuctionContext";
import { UserInputProvider } from "../../contexts/UserInputContext";
import { TicketingFrontendProvider } from "../../providers/FrontendTicketingProvider";

import { ZeffyHead } from "@/components/Head/ZeffyHead";
import NotFoundWithProviders from "@/components/NotFound/NotFoundWithProviders";
import { TicketingVersionSelector } from "@/components/TicketingContainerV1/TicketingVersionSelector";
import { addCacheTagHeaderToServerResponse } from "@/helpers/cache";
import { getSSRTicketing, getTranslation, provideOrganization } from "@/helpers/getStaticProps";
import { usePageScrollUp } from "@/hooks/usePageScrollUp";
import { useTheme } from "@/hooks/useTheme";
import { ServerSideContext } from "@/types/app";
import { TicketingFrontendPageOutput } from "@/types/trpc";

type TicketingPageProps =
  | {
      ticketing: TicketingFrontendPageOutput;
      isDeleted?: false;
    }
  | {
      ticketing?: null;
      isDeleted: true;
    };

const TicketingPage = ({ ticketing, isDeleted }: TicketingPageProps) => {
  usePageScrollUp();
  const theme = useTheme({ color: ticketing?.color });

  if (isDeleted) {
    return (
      <>
        <ZeffyHead />
        <NotFoundWithProviders deletedForm />
      </>
    );
  }

  return (
    <>
      {/* Nothing should wrap or block the Head component */}
      <TicketingHead ticketing={ticketing} country={ticketing.organization?.country} />
      <AppFormProvider
        theme={theme}
        organization={ticketing.organization}
        formType={FormType.Ticketing}
        formData={ticketing}
        fields={ticketing.ticketingFields}
        isEmbed={false}
        shouldDisplayLogin={false}
        isFundraiser={false}
        themeColor={ticketing.color}
      >
        <TicketingFrontendProvider ticketing={ticketing}>
          <FrontendAuctionProvider ticketing={ticketing}>
            <UserInputProvider>
              <TicketingVersionSelector ssrTicketing={ticketing} />
            </UserInputProvider>
          </FrontendAuctionProvider>
        </TicketingFrontendProvider>
      </AppFormProvider>
    </>
  );
};

export const getServerSideProps = async (context: ServerSideContext<{ ticketingId: string }>) => {
  const requestId = (context.req?.headers?.["x-request-id"] as string) || v4();
  const { ticketingId } = context.params;

  addCacheTagHeaderToServerResponse(context.res, ticketingId);

  const locale = context.locale.toUpperCase() as Locales;

  try {
    const [ticketingResponse, translations] = await Promise.all([
      getSSRTicketing({ ticketingIdOrUrlPath: ticketingId, requestId }),
      getTranslation(context.locale),
    ]);

    if (ticketingResponse.error) {
      const code = ticketingResponse.error.code;
      if (code === FormErrors.FormDeleted) {
        if (context.res) {
          context.res.statusCode = 410;
        }

        console.log(`Form deleted - redirecting to Gonne ticketingId=${ticketingId} ssrId=${requestId}`);
        return {
          props: {
            ...translations,
            isDeleted: true,
          },
        };
      }

      console.log(`NEXT_ERROR: form not found - redirecting to notFound ticketingId=${ticketingId} ssrId=${requestId}`);
      return { props: {}, notFound: true };
    }

    return {
      props: {
        ...translations,
        ticketing: ticketingResponse.data.organization
          ? ticketingResponse.data
          : provideOrganization(ticketingResponse.data, locale),
      },
    };
  } catch (error) {
    console.log(
      `NEXT_ERROR: request failed - redirecting to notFound ticketingId=${ticketingId} ssrId=${requestId}`,
      error
    );
    return { props: {}, notFound: true };
  }
};

export default TicketingPage;
